import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DimensionEnums } from "models/enums";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { IncomingInvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";
import { FormFieldConstants } from "utils/constants";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { CreditPaymentHandling } from "models/enums/incomingInvoice";
//#endregion
//#region components
import { periodisationRowsSchema, useDimensionMaps } from "components/common";
import useInvoiceCategorySuggestions from "../useInvoiceCategorySuggestions";
import { useBookingTreeService } from "serviceHooks";
import { costForwardingValidationFields } from "components/costForwardingMarking";
//#endregion
import * as Yup from "yup";
import { DimensionHelper, PeriodisationRowsHelper, UtilsHelper } from "utils/helpers";

function useIncomingInvoiceTodoFormHelpers(
  invoice: WintSuperkollAPIModel.IncomingInvoice,
  hasPermissionToEditPaymentInformation?: boolean
) {
  const bookingTreeService = useBookingTreeService();
  const { dimensions, forcedDimensionMap } = useDimensionMaps(DimensionEnums.DimensionCategory.IncomingInvoice);
  const companyNoVat = useSelector((store: AppState) => store.auth.selectedCompany.noVat);

  const dimensionAmount = React.useMemo(
    () => PeriodisationRowsHelper.netAmount(invoice.amount, invoice.tax, companyNoVat),
    [companyNoVat, invoice]
  );

  const { defaultAccount, suggestions: accountSuggestions } = useInvoiceCategorySuggestions<SelectionItem>(
    invoice.id,
    invoice.isAutoInvoice,
    bookingTreeService.convertToSelectionItems
  );

  const getCreditPaymentHandlingStatus = (
    invoice: WintSuperkollAPIModel.IncomingInvoice
  ): CreditPaymentHandling | undefined => {
    // If invoice is in received state, no credit payment handling is selected
    if (invoice.state === WintSuperkollAPIModel.IncomingInvoiceState.Received) {
      return undefined;
    }

    // If there's a payment handling comment, use "Other" option
    if (!UtilsHelper.isEmpty(invoice.paymentHandlingComment)) {
      return CreditPaymentHandling.Other;
    }

    if (invoice.isAlreadyPaid) {
      return CreditPaymentHandling.Refund;
    }

    return CreditPaymentHandling.UseAgainstFutureInvoices;
  };

  const initialValues = React.useMemo(
    (): IncomingInvoiceFormTypes.Todo => ({
      account: {
        id: invoice.account || (defaultAccount && (defaultAccount.id as number)) || undefined,
        value: invoice.accountName || (defaultAccount && (defaultAccount.value as string)) || "",
      },
      accountSuggestions: accountSuggestions,
      dimensions: dimensions ? DimensionHelper.getValidList(invoice.dimensionList ?? [], dimensions) : [],
      periodisation: PeriodisationRowsHelper.fromInvoice(invoice),
      allowPeriodisation: invoice.allowPeriodisation,
      showDimensions: dimensions && dimensions.length > 0,
      showAccount: !invoice.isTaxInvoice,
      isAlreadyPaid: invoice.isAlreadyPaid,
      creditPaymentHandling: getCreditPaymentHandlingStatus(invoice),
      paymentHandlingInformation: invoice.paymentHandlingComment,
      additionalInfoComment: invoice.additionalInfoComment,
      iban: invoice.iBAN,
      bic: invoice.bIC ?? invoice.supplier?.bIC,
      street: invoice.supplier?.street,
      street2: invoice.supplier?.street2,
      city: invoice.supplier?.city,
      zipCode: invoice.supplier?.zipCode,
      country: invoice.supplier?.country,
    }),
    [invoice.id, defaultAccount, dimensions, invoice.isTaxInvoice, invoice, accountSuggestions]
  );

  const additionalInfoNeededAccounts = useSelector(
    (state: AppState) => state.auth.selectedCompany.additionalInfoNeededInvoiceAccounts
  );

  const getAccountAdditionalInfoType = (id?: number | string) => {
    const account = additionalInfoNeededAccounts?.find((m) => m.accountId === id);
    return account?.additionalInformationRequired ?? WintSuperkollAPIModel.AdditionalInformationRequired.None;
  };

  const getValidationSchema = (
    isInternationalPayment: boolean,
    isSEPAPayment: boolean,
    skipInternationalPaymentValidations: boolean
  ) => {
    const supplierAddressRequired =
      isInternationalPayment && !skipInternationalPaymentValidations && !!hasPermissionToEditPaymentInformation;

    const supplierAddressField = () =>
      supplierAddressRequired ? Yup.string().required(TranslationKeys.Validations.Required) : Yup.string();

    return Yup.object<IncomingInvoiceFormTypes.Todo>().shape(
      {
        // TODO move to helpers method
        account: Yup.object<AutocompleteListItem>({
          value: Yup.string(),
          id: Yup.number().required(TranslationKeys.Validations.Required),
        })
          .default(undefined)
          .required(TranslationKeys.Validations.Required),
        accountSuggestions: Yup.array(),
        dimensions: Yup.array()
          .of(
            Yup.object<WintSuperkollAPIModel.DimensionData>({
              dimensionId: Yup.string(),
              amount: Yup.number(),
            })
          )
          // eslint-disable-next-line no-empty-pattern
          .when([], ([], schema: ArraySchema<WintSuperkollAPIModel.DimensionData>) =>
            forcedDimensionMap.length > 0 ? schema.required("") : schema
          )
          .checkForcedDimensions(forcedDimensionMap)
          .validateDimensionAmounts(dimensions, dimensionAmount),
        periodisation: periodisationRowsSchema(Math.abs(dimensionAmount)),
        allowPeriodisation: Yup.boolean(),
        showDimensions: Yup.boolean(),
        showAccount: Yup.boolean(),
        isAlreadyPaid: Yup.boolean(),
        creditPaymentHandling: invoice.isCredit
          ? Yup.number().required(TranslationKeys.Validations.Required)
          : Yup.number(),
        paymentHandlingInformation: Yup.string().when(
          [FormFieldConstants.IncomingInvoice.Todo.CreditPaymentHandling],
          ([creditPaymentHandling], schema: Yup.StringSchema) =>
            creditPaymentHandling === CreditPaymentHandling.Other
              ? schema.required(TranslationKeys.Validations.Required)
              : schema
        ),
        additionalInfoComment: Yup.string()
          .when(
            FormFieldConstants.IncomingInvoice.Todo.Account,
            ([account], schema: Yup.StringSchema) =>
              (!invoice.isAutoInvoice &&
                getAccountAdditionalInfoType(account?.id) ===
                  WintSuperkollAPIModel.AdditionalInformationRequired.Comment &&
                schema.required(TranslationKeys.Validations.Required)) ||
              schema
          )
          .when(
            [
              FormFieldConstants.IncomingInvoice.Todo.Account,
              FormFieldConstants.IncomingInvoice.Todo.AdditionalInfoAttachments,
            ],
            ([account, additionaAttachment], schema: Yup.StringSchema) =>
              (!invoice.isAutoInvoice &&
                getAccountAdditionalInfoType(account?.id) ===
                  WintSuperkollAPIModel.AdditionalInformationRequired.FileOrComment &&
                !additionaAttachment?.length &&
                schema.required(TranslationKeys.Validations.Required)) ||
              schema
          ),
        additionalInfoAttachments: Yup.array()
          .of(
            Yup.object<UploadedFile>({
              contentType: Yup.string(),
              fileName: Yup.string(),
              fileSize: Yup.number(),
              data: Yup.string(),
            })
          )
          .when(
            [
              FormFieldConstants.IncomingInvoice.Todo.Account,
              FormFieldConstants.IncomingInvoice.Todo.AdditionalInfoComment,
            ],
            ([account, additionalComment]: [AutocompleteListItem, string], schema: ArraySchema<UploadedFile>) =>
              (!invoice.isAutoInvoice &&
                getAccountAdditionalInfoType(account?.id) ===
                  WintSuperkollAPIModel.AdditionalInformationRequired.FileOrComment &&
                UtilsHelper.isEmpty(additionalComment) &&
                schema.required(TranslationKeys.Validations.Required)) ||
              schema
          ),
        // eslint-disable-next-line no-empty-pattern
        iban: Yup.string().when([], ([], schema: Yup.StringSchema) =>
          isInternationalPayment && !skipInternationalPaymentValidations && hasPermissionToEditPaymentInformation
            ? schema.required(TranslationKeys.Validations.Required)
            : schema
        ),
        // eslint-disable-next-line no-empty-pattern
        bic: Yup.string().when([], ([], schema: Yup.StringSchema) =>
          isInternationalPayment &&
          !isSEPAPayment &&
          !skipInternationalPaymentValidations &&
          hasPermissionToEditPaymentInformation
            ? schema.required(TranslationKeys.Validations.Required)
            : schema
        ),
        street: supplierAddressField(),
        street2: Yup.string(),
        city: supplierAddressField(),
        zipCode: supplierAddressField(),
        country: supplierAddressField(),
        ...costForwardingValidationFields,
      },
      [
        [
          FormFieldConstants.IncomingInvoice.Todo.AdditionalInfoComment,
          FormFieldConstants.IncomingInvoice.Todo.AdditionalInfoAttachments,
        ],
      ]
    );
  };

  const validationSchema = React.useMemo(
    () => getValidationSchema(invoice.isInternationalPayment, invoice.isSEPAPayment, false),
    [
      forcedDimensionMap,
      dimensions,
      dimensionAmount,
      invoice,
      hasPermissionToEditPaymentInformation,
      additionalInfoNeededAccounts,
    ]
  );

  const isFormValid = async (
    formRef: React.RefObject<FormHandles>,
    isInternationalPayment: boolean,
    isSEPAPayment: boolean
  ) => {
    const validationSchema = getValidationSchema(isInternationalPayment, isSEPAPayment, true);
    return formRef?.current?.validate
      ? await formRef?.current.validate(TranslationKeys.Validations.FormContainsErrors, validationSchema)
      : false;
  };

  return {
    getValidationSchema,
    validationSchema,
    initialValues,
    accountSuggestions,
    dimensionAmount,
    getAccountAdditionalInfoType,
    isFormValid,
  };
}

export default useIncomingInvoiceTodoFormHelpers;
